//#region IMPORT
import Commons from '../../../Services/Common/Common';
import Global from '../../../Services/Common/Global';
import { showSnackbar } from '../../../Reducer/SnackbarReducer';
import dayjs from 'dayjs';
//#endregion

/**
 * 안전작업 관리 - 안전작업 허가서 (SAF_BASIC_02) 전용 로직
 * other 케이스에서 사용
 */

// 이미지를 Base64로 변환하는 함수
const ConvertImageToBase64 = async (url) => {
  try {
    const response = await fetch(url);
    if (!response.ok) return '';
    const blob = await response.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        resolve(reader.result);
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.error('이미지 변환 오류:', error);
    return '';
  }
};

// 작업종류 조회 함수
const SearchLogic2 = async (_PM_CD) => {
  try {
    const result = await Commons.Axios(
      'post',
      Global.base.useUrl + Global.urls.Execute.execute,
      [
        {
          procedureName: 'SP_PERMIT_W_R2',
          _PM_CD: Commons.EncryptAES(_PM_CD) || '',
        },
      ]
    );

    const data = result.data;
    if (data.length === 0 || data[0]?.type === 'Error') {
      return [];
    }
    return data;
  } catch (e) {
    console.error('SearchLogic2 실패:', e);
    return [];
  }
};

// 작업별 상세 조회 함수
const SearchLogic3 = async (_PM_CD) => {
  try {
    const result = await Commons.Axios(
      'post',
      Global.base.useUrl + Global.urls.Execute.execute,
      [
        {
          procedureName: 'SP_PERMIT_W_R4',
          _PM_CD: Commons.EncryptAES(_PM_CD) || '',
        },
      ]
    );

    const data = result.data;
    if (data.length === 0 || data[0]?.type === 'Error') {
      return [];
    }
    return data;
  } catch (e) {
    console.error('SearchLogic3 실패:', e);
    return [];
  }
};

// 안전작업 허가서 조회 함수
export const SearchLogic1 = async (procedureParams, dispatch, setLoading) => {
  setLoading(true);

  try {
    const result = await Commons.Axios(
      'post',
      Global.base.useUrl + Global.urls.Execute.execute,
      [
        {
          procedureName: procedureParams.procedureName,
          ...procedureParams,
        },
      ]
    );

    let data = result.data;

    if (data.length === 0) {
      dispatch(
        showSnackbar({
          message: '조회된 데이터가 없습니다.',
          type: 'info',
        })
      );
      return [];
    } else if (data[0]?.type === 'Error') {
      dispatch(showSnackbar({ message: data[0].message, type: 'error' }));
      return [];
    } else {
      // 날짜 포맷팅 처리
      data.forEach((item) => {
        const date1 = new Date(item.WORK_START_DATE);
        const date2 = new Date(item.WORK_END_DATE);

        const year = date1.getFullYear();
        const month = String(date1.getMonth() + 1).padStart(2, '0');
        const day = String(date1.getDate()).padStart(2, '0');
        const hours = String(date1.getHours()).padStart(2, '0');
        const minutes = String(date1.getMinutes()).padStart(2, '0');

        const year2 = date2.getFullYear();
        const month2 = String(date2.getMonth() + 1).padStart(2, '0');
        const day2 = String(date2.getDate()).padStart(2, '0');
        const hours2 = String(date2.getHours()).padStart(2, '0');
        const minutes2 = String(date2.getMinutes()).padStart(2, '0');

        item.WORK_START_DATE = `${year}-${month}-${day} ${hours}:${minutes}`;
        item.WORK_END_DATE = `${year2}-${month2}-${day2} ${hours2}:${minutes2}`;
        item.CREATE_DATE = item.CREATE_DATE.substr(0, 10);
      });

      return Commons.InsertNumber(data);
    }
  } catch (e) {
    console.error(e);
    dispatch(showSnackbar({ message: e.message, type: 'error' }));
    return [];
  } finally {
    setLoading(false);
  }
};

// 안전작업 허가서 인쇄 함수
export const PrintLogic1 = async (
  documentData, // 선택된 항목 데이터
  dispatch,
  setLoading
) => {
  if (!documentData) {
    dispatch(
      showSnackbar({
        message: '항목을 선택하세요.',
        type: 'warning',
      })
    );
    return null;
  }

  setLoading(true);

  try {
    // documentData가 배열인 경우 첫 번째 항목 사용
    const selectedItem = Array.isArray(documentData)
      ? documentData[0]
      : documentData;

    // STATUS가 "승인"이 아니면 인쇄 불가
    if (selectedItem?.STATUS !== '승인') {
      dispatch(
        showSnackbar({
          message: '승인된 항목만 인쇄할 수 있습니다.',
          type: 'warning',
        })
      );
      return null;
    }

    const _PM_CD = selectedItem.PM_CD; // 안전작업허가서 코드

    // 작업종류 조회
    const tableData1 = await SearchLogic2(_PM_CD);

    // 작업별 상세 조회
    const tableData2 = await SearchLogic3(_PM_CD);

    // 부서장 사인
    let cleanBase64_dep = null;
    if (selectedItem.DEP_SIGN_PATH) {
      try {
        const imageUrl_dep =
          Global.file.useUrl + 'files/' + selectedItem.DEP_SIGN_PATH;
        const base64Image_dep = await ConvertImageToBase64(imageUrl_dep);
        cleanBase64_dep = base64Image_dep.replace(
          /^data:image\/[a-z]+;base64,/,
          ''
        );
      } catch (error) {
        cleanBase64_dep = null;
      }
    }

    // 안전관리자 사인
    let cleanBase64_sm = null;
    if (selectedItem.SM_SIGN_PATH) {
      try {
        const imageUrl_sm =
          Global.file.useUrl + 'files/' + selectedItem.SM_SIGN_PATH;
        const base64Image_sm = await ConvertImageToBase64(imageUrl_sm);
        cleanBase64_sm = base64Image_sm.replace(
          /^data:image\/[a-z]+;base64,/,
          ''
        );
      } catch (error) {
        cleanBase64_sm = null;
      }
    }

    // 테이블 headerData
    const headerData = {
      ...selectedItem,
      DEP_SIGN_PATH: selectedItem.DEP_SIGN_PATH ? cleanBase64_dep : null,
      SM_SIGN_PATH: selectedItem.SM_SIGN_PATH ? cleanBase64_sm : null,
    };

    // 작업종류 체크박스 데이터 생성
    const workTypesArray = tableData1[0]?.WORK_TYPES?.split(',') || [];
    const TableDataMain = [
      {
        WT_001: workTypesArray.includes('WT_001'),
        WT_002: workTypesArray.includes('WT_002'),
        WT_003: workTypesArray.includes('WT_003'),
        WT_004: workTypesArray.includes('WT_004'),
        WT_005: workTypesArray.includes('WT_005'),
        WT_006: workTypesArray.includes('WT_006'),
      },
    ];

    // 작업별 상세 체크 데이터 생성
    const tableDataSingleRow = {};
    tableData2.forEach((item) => {
      const keyBase = item.WORK_TYPE_DTL;
      tableDataSingleRow[`CHECK_CONFIRM_${keyBase}`] = '';
      tableDataSingleRow[`CHECK_NOT_APPLICABLE_${keyBase}`] = '';

      if (item.ITEM_CHECK === '0') {
        // 확인
        tableDataSingleRow[`CHECK_CONFIRM_${keyBase}`] = 'true';
      } else if (item.ITEM_CHECK === '1') {
        // 해당없음
        tableDataSingleRow[`CHECK_NOT_APPLICABLE_${keyBase}`] = 'true';
      }
    });

    const serializedCheckData = JSON.stringify([tableDataSingleRow]);

    const printData = {
      FileName: 'PER_BASIC',
      HeaderData: JSON.stringify(headerData),
      TableDataMain,
      TableData1: serializedCheckData,
      TableData2: serializedCheckData,
      TableData3: serializedCheckData,
      TableData4: serializedCheckData,
      TableData5: serializedCheckData,
      TableData6: serializedCheckData,
    };

    const response = await Commons.Axios(
      'report',
      Global.base.useUrl + Global.urls.Reports.reports,
      printData
    );

    // response.data가 blob인 경우 그대로 반환
    if (response.data instanceof Blob) {
      return response.data;
    }
    // response.data가 다른 형태인 경우 blob으로 변환
    else if (response.data) {
      return new Blob([response.data], { type: 'application/pdf' });
    } else {
      console.error('PrintLogic1: 응답 데이터가 없습니다.');
      return null;
    }
  } catch (e) {
    console.error(e);
    dispatch(
      showSnackbar({
        message: e.message || '인쇄 데이터 생성에 실패했습니다.',
        type: 'error',
      })
    );
    return null;
  } finally {
    setLoading(false);
  }
};
